/*
 * Product-list tracking: which cards a shopper saw on a collection page, and
 * which one they tapped, by the position it held on screen.
 *
 * GA4 here is fed by a Shopify pixel, and a pixel cannot read anything the
 * theme puts on window. Shopify.analytics.publish is the one way in, so this
 * publishes two custom events and the custom pixel in
 * notes/tracking/ga4-custom-pixel.js turns them into GA4's own
 * view_item_list and select_item. Nothing here talks to Google directly.
 *
 * Markup it reads, written by the collection sections:
 *   [data-jj-list-id][data-jj-list-name]   the grid
 *   [data-jj-item-id]                      a card, with -name, -variant, -price
 *
 * POSITION IS MEASURED ON SCREEN, NOT IN THE HTML. The premium grid orders
 * cards with CSS `order`, the editorial grid moves cards about in JS, filters
 * hide some and sold-out cards wait for the last page. So a card's index is
 * its rank among the cards visible right now, top to bottom then left to
 * right: what the shopper actually saw as "the third one".
 *
 * A VIEW IS A CARD SCROLLED HALF INTO VIEW, once per card per page. Counting
 * every card rendered would credit position 40 with views nobody had, and
 * clicks per view by position is the whole point.
 */
(function () {
  if (window.__jjListTracking) return;
  window.__jjListTracking = true;

  var VIEW_EVENT = 'jj_view_item_list';
  var CLICK_EVENT = 'jj_select_item';
  var FLUSH_MS = 1000;
  var MAX_BATCH = 30;

  var seen = new WeakSet();
  var watched = new WeakSet();
  var queues = new Map();
  var flushTimer = null;

  function publish(name, data) {
    var api = window.Shopify && window.Shopify.analytics;
    if (!api || typeof api.publish !== 'function') return;
    try {
      api.publish(name, data);
    } catch (err) {
      /* Measurement only; never let it break the page. */
    }
  }

  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;
  }

  function isVisible(el) {
    return el.getClientRects().length > 0;
  }

  /* Rank of `card` among the visible cards of its grid, plus the grid's
     column count. Cards whose tops sit within a few pixels share a row;
     image heights differ by a pixel or two between cards. */
  function layout(grid, card) {
    var cards = Array.prototype.filter.call(grid.querySelectorAll('[data-jj-item-id]'), isVisible);
    var boxes = cards.map(function (el) {
      var r = el.getBoundingClientRect();
      return { el: el, top: Math.round(r.top), left: Math.round(r.left) };
    });
    boxes.sort(function (a, b) {
      return Math.abs(a.top - b.top) > 4 ? a.top - b.top : a.left - b.left;
    });

    var cols = 0;
    if (boxes.length) {
      var firstTop = boxes[0].top;
      while (cols < boxes.length && Math.abs(boxes[cols].top - firstTop) <= 4) cols++;
    }

    var index = 0;
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i].el === card) {
        index = i + 1;
        break;
      }
    }
    return { index: index, cols: cols || 1 };
  }

  function listOf(grid) {
    return {
      item_list_id: grid.getAttribute('data-jj-list-id') || '',
      item_list_name: grid.getAttribute('data-jj-list-name') || ''
    };
  }

  function itemOf(grid, card) {
    var list = listOf(grid);
    var pos = layout(grid, card);
    var price = parseFloat(card.getAttribute('data-jj-item-price'));
    var item = {
      item_id: card.getAttribute('data-jj-item-id'),
      item_name: card.getAttribute('data-jj-item-name') || '',
      index: pos.index,
      item_list_id: list.item_list_id,
      item_list_name: list.item_list_name
    };
    var variant = card.getAttribute('data-jj-item-variant');
    if (variant) item.item_variant = variant;
    if (!isNaN(price)) item.price = price;
    return { item: item, cols: pos.cols };
  }

  /* ---- Views ---- */

  function flush() {
    flushTimer = null;
    queues.forEach(function (items, grid) {
      while (items.length) {
        var batch = items.splice(0, MAX_BATCH);
        var list = listOf(grid);
        publish(VIEW_EVENT, {
          item_list_id: list.item_list_id,
          item_list_name: list.item_list_name,
          currency: currency(),
          items: batch
        });
      }
    });
    queues.clear();
  }

  function queueView(grid, card) {
    if (seen.has(card)) return;
    seen.add(card);
    var entry = itemOf(grid, card);
    if (!entry.item.index) return;
    if (!queues.has(grid)) queues.set(grid, []);
    queues.get(grid).push(entry.item);
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var card = entry.target;
              var grid = card.closest('[data-jj-list-id]');
              observer.unobserve(card);
              if (grid) queueView(grid, card);
            });
          },
          { threshold: 0.5 }
        )
      : null;

  function scan() {
    if (!observer) return;
    document.querySelectorAll('[data-jj-list-id] [data-jj-item-id]').forEach(function (card) {
      if (watched.has(card)) return;
      watched.add(card);
      observer.observe(card);
    });
  }

  /* ---- Clicks ---- */

  /* Capture phase, so it runs before any handler that navigates. Only links
     to a product count: a swatch, the favourite heart or quick add is not
     choosing the item from the list. */
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest && event.target.closest('a[href*="/products/"]');
      if (!link) return;
      var card = link.closest('[data-jj-item-id]');
      var grid = card && card.closest('[data-jj-list-id]');
      if (!grid) return;

      flush();
      var entry = itemOf(grid, card);
      var list = listOf(grid);
      publish(CLICK_EVENT, {
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        currency: currency(),
        items: [entry.item],
        jj_row: Math.ceil(entry.item.index / entry.cols),
        jj_cols: entry.cols
      });
    },
    true
  );

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });

  /* Infinite scroll and filters add or replace cards after load. */
  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      scan();
    });
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
